import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { formatDate } from './date';

const titleCase = (s?: string) =>
  (s ?? '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const fmtTime = (t?: string) => (t ? dayjs(t).format('hh:mm A') : '-');

const fmtDur = (secs: number) => {
  const m = Math.round((secs || 0) / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

// Mirrors the rendering in incentiveCalculation/columns.tsx so the exported
// file matches exactly what the on-screen table shows.
const toExportRow = (r: any) => ({
  Branch:        r.branch ?? '-',
  Client:        r.name ?? '-',
  Phone:         r.phoneNumber ?? '-',
  Service:       `${titleCase(r.serviceType) || '-'}${r.subCategory ? ` · ${titleCase(r.subCategory)}` : ''}`,
  Type:          r.type ? titleCase(r.type) : '-',
  Plan:          r.planName ?? '-',
  'Trainer Name': r.trainerName ?? '-',
  'Session Date': r.sessionDate ? formatDate(r.sessionDate) : '-',
  'Start Time':  r.sessionDate ? fmtTime(r.sessionDate) : '-',
  'End Time':    r.endedAt ? fmtTime(r.endedAt) : '-',
  Duration:      r.activeDurationMinutes != null ? `${r.activeDurationMinutes}m`
                   : r.activeSeconds != null ? fmtDur(r.activeSeconds) : '-',
  'Per Session Amount': (() => {
    const v = r.perSessionAmount ?? r.sessionValue ?? r.value ?? r.amount;
    return v != null ? Number(v) : '-';
  })(),
  Status: r.status ?? '-',
});

interface ExportMeta {
  trainerName?: string;
  fromDate: string;
  toDate: string;
}

const buildFilename = (meta: ExportMeta, ext: string) => {
  const namePart = meta.trainerName ? `-${meta.trainerName.replace(/\s+/g, '_')}` : '';
  return `Incentive-Calculation${namePart}-${meta.fromDate}-to-${meta.toDate}.${ext}`;
};

export const exportIncentiveSessionsToExcel = (sessions: any[], meta: ExportMeta) => {
  const rows = sessions.map(toExportRow);
  const headers = Object.keys(rows[0] ?? toExportRow({}));

  const title = `Incentive Calculation${meta.trainerName ? ` - ${meta.trainerName}` : ''} (${meta.fromDate} to ${meta.toDate})`;

  // Row 1: title (merged across all columns), row 2: blank spacer, row 3+:
  // header + data — matches the report-style heading the PDF export shows.
  const worksheet = XLSX.utils.aoa_to_sheet([[title]]);
  XLSX.utils.sheet_add_json(worksheet, rows, { origin: 'A3', header: headers });
  worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];

  // Auto-fit each column to its widest cell (header or any row's value) so
  // nothing gets clipped, instead of Excel's default fixed narrow width.
  worksheet['!cols'] = headers.map(h => {
    const maxLen = rows.reduce((max, row) => {
      const v = (row as any)[h];
      return Math.max(max, v == null ? 0 : String(v).length);
    }, h.length);
    return { wch: maxLen + 2 };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sessions');
  XLSX.writeFile(workbook, buildFilename(meta, 'xlsx'));
};
